import { useState } from 'react'
import { Routes, Route, NavLink } from 'react-router-dom'
import { useDataStore } from '../stores/dataStore'
import type { Company, Contact } from '../types'
import { Button, Input } from '../components/ui'
import CompanyPanel from '../components/ledger/CompanyPanel'
import ContactPanel from '../components/ledger/ContactPanel'
import AddCompanyModal from '../components/ledger/AddCompanyModal'
import AddContactModal from '../components/ledger/AddContactModal'

function Companies() {
  const { companies } = useDataStore()
  const [search, setSearch] = useState('')
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)

  const filteredCompanies = companies.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.industry?.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="flex gap-6">
      <div className="flex-1">
        <div className="flex items-center gap-4 mb-4">
          <Input
            placeholder="Search companies..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs"
          />
          <Button variant="primary" onClick={() => setIsAddModalOpen(true)}>
            + Add company
          </Button>
        </div>

        {filteredCompanies.length === 0 ? (
          <div className="empty-state">
            <p className="empty-state-text">
              {search ? 'No results for that search.' : 'No companies added.'}
            </p>
          </div>
        ) : (
          <div className="bg-bg-surface border border-border rounded-lg overflow-hidden">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Industry</th>
                  <th>Website</th>
                </tr>
              </thead>
              <tbody>
                {filteredCompanies.map((company) => (
                  <tr
                    key={company.id}
                    onClick={() => setSelectedCompany(company)}
                    onKeyDown={(e) => e.key === 'Enter' && setSelectedCompany(company)}
                    tabIndex={0}
                    className={`
                      cursor-pointer
                      ${selectedCompany?.id === company.id ? 'bg-action-focus' : ''}
                    `}
                  >
                    <td className="font-medium">{company.name}</td>
                    <td className="text-text-secondary">
                      {company.industry || '—'}
                    </td>
                    <td className="text-text-secondary">
                      {company.website ? (
                        <a
                          href={company.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="hover:text-action-primary underline"
                        >
                          {company.website.replace(/^https?:\/\//, '')}
                        </a>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <AddCompanyModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
        />
      </div>

      {selectedCompany && (
        <CompanyPanel
          company={selectedCompany}
          onClose={() => setSelectedCompany(null)}
        />
      )}
    </div>
  )
}

function Contacts() {
  const { contacts, companies } = useDataStore()
  const [search, setSearch] = useState('')
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)

  const filteredContacts = contacts.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.role?.toLowerCase().includes(search.toLowerCase())
  )

  const getCompanyName = (companyId?: string) => {
    if (!companyId) return '—'
    return companies.find((c) => c.id === companyId)?.name || '—'
  }

  return (
    <div className="flex gap-6">
      <div className="flex-1">
        <div className="flex items-center gap-4 mb-4">
          <Input
            placeholder="Search contacts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs"
          />
          <Button variant="primary" onClick={() => setIsAddModalOpen(true)}>
            + Add contact
          </Button>
        </div>

        {filteredContacts.length === 0 ? (
          <div className="empty-state">
            <p className="empty-state-text">
              {search ? 'No results for that search.' : 'No contacts added.'}
            </p>
          </div>
        ) : (
          <div className="bg-bg-surface border border-border rounded-lg overflow-hidden">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Email</th>
                  <th>Role</th>
                </tr>
              </thead>
              <tbody>
                {filteredContacts.map((contact) => (
                  <tr
                    key={contact.id}
                    onClick={() => setSelectedContact(contact)}
                    onKeyDown={(e) => e.key === 'Enter' && setSelectedContact(contact)}
                    tabIndex={0}
                    className={`
                      cursor-pointer
                      ${selectedContact?.id === contact.id ? 'bg-action-focus' : ''}
                    `}
                  >
                    <td className="font-medium">{contact.name}</td>
                    <td className="text-text-secondary">
                      {getCompanyName(contact.companyId)}
                    </td>
                    <td className="text-text-secondary">
                      {contact.email || '—'}
                    </td>
                    <td className="text-text-secondary">
                      {contact.role || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <AddContactModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
        />
      </div>

      {selectedContact && (
        <ContactPanel
          contact={selectedContact}
          onClose={() => setSelectedContact(null)}
        />
      )}
    </div>
  )
}

export default function Ledger() {
  return (
    <div>
      <h1 className="text-xl font-semibold text-text-primary mb-6">Directory</h1>
      
      <div className="flex gap-1 mb-6">
        <NavLink
          to="/ledger/companies"
          className={({ isActive }) =>
            `px-4 py-2 text-sm font-medium rounded-[6px] transition-colors duration-150 ${
              isActive
                ? 'bg-action-primary text-white'
                : 'text-text-secondary hover:text-text-primary hover:bg-[#F5F5F3]'
            }`
          }
        >
          Companies
        </NavLink>
        <NavLink
          to="/ledger/contacts"
          className={({ isActive }) =>
            `px-4 py-2 text-sm font-medium rounded-[6px] transition-colors duration-150 ${
              isActive
                ? 'bg-action-primary text-white'
                : 'text-text-secondary hover:text-text-primary hover:bg-[#F5F5F3]'
            }`
          }
        >
          Contacts
        </NavLink>
      </div>

      <Routes>
        <Route index element={<Companies />} />
        <Route path="companies" element={<Companies />} />
        <Route path="contacts" element={<Contacts />} />
      </Routes>
    </div>
  )
}
